import { useCallback, useState, useMemo } from 'react'
import { View, StyleSheet, FlatList } from 'react-native'
import { Text, Button, Chip, List, Searchbar } from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../../../../hooks/theme.hook'
import {
  useAvailableNodesQuery,
  useTransferClusterToNodeMutation,
  ClusterByIdQuery,
} from '../../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../../utils/message-type'
import * as Sentry from '@sentry/react-native'
import { useNode } from '../../../../../contexts/current-node.provider'
import {
  ERROR_CODES,
  getApolloErrorCode,
} from '../../../../../utils/error-utils'
import { MdNode } from '../../nodes-list/types'
import { IconNode } from '../../../../../utils/meldd-icons'
import { MelddModal } from '../../../../../@ui'

interface TransferClusterToNodeModalProps {
  visible: boolean
  onDismiss: () => void
  cluster: ClusterByIdQuery['clusterById']
}

export const TransferClusterToNodeModal = ({
  visible,
  onDismiss,
  cluster,
}: TransferClusterToNodeModalProps) => {
  const { t } = useTranslation('clusters')
  const { t: tNodes } = useTranslation('nodes')
  const theme = useTheme()
  const { setCurrentNode } = useNode()
  const [selectedNode, setSelectedNode] = useState<MdNode | undefined>(
    undefined
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  const { data, loading } = useAvailableNodesQuery({
    fetchPolicy: 'cache-and-network',
  })

  const [transferClusterToNode, { loading: isTransferring }] =
    useTransferClusterToNodeMutation()

  // Filter out current node and get available nodes
  const availableNodes = useMemo(() => {
    if (!data?.availableNodes) {
      return []
    }
    return data.availableNodes.filter((node) => node.id !== cluster.nodeId)
  }, [data?.availableNodes, cluster.nodeId])

  // Filter nodes by search query
  const filteredNodes = useMemo(() => {
    if (!searchQuery) {
      return availableNodes
    }
    return availableNodes.filter((node) =>
      node.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [availableNodes, searchQuery])

  const handleTransfer = useCallback(async () => {
    if (!selectedNode) {
      return
    }

    try {
      const result = await transferClusterToNode({
        variables: {
          input: {
            clusterId: cluster.id,
            newNodeId: selectedNode.id,
          },
        },
        refetchQueries: ['clusterById', 'availableClusters'],
      })

      if (result.data?.transferClusterToNode) {
        // Select the new node to avoid navigation errors
        setCurrentNode(selectedNode.id)
        Toast.show(t('cluster-transferred-successfully'), MessageType.info)
        onDismiss()
      }
    } catch (error: any) {
      console.error(error)
      Sentry.captureException(error)

      const errorCode = getApolloErrorCode(error, 0)
      let errorMessage = (error as Error).message

      if (errorCode === ERROR_CODES.NOT_FOUND) {
        errorMessage = t('new-node-does-not-exist')
      } else if (errorCode === ERROR_CODES.FORBIDDEN) {
        errorMessage = t('you-are-not-a-moderator')
      } else if (errorCode === ERROR_CODES.METHOD_NOT_ALLOWED) {
        errorMessage = t('no-permissions-in-new-node')
      } else if (errorCode === ERROR_CODES.UNAUTHORIZED) {
        errorMessage = t('not-logged-in')
      }

      Toast.show(errorMessage, MessageType.error)
    }
  }, [
    selectedNode,
    cluster.id,
    transferClusterToNode,
    setCurrentNode,
    t,
    onDismiss,
  ])

  const styles = useMemo(
    () =>
      StyleSheet.create({
        selectorContainer: {
          marginVertical: theme.spacing(2),
        },
        dropdownContainer: {
          position: 'relative',
          zIndex: 1,
        },
        dropdownList: {
          maxHeight: 300,
          backgroundColor: theme.colors.surface,
          borderRadius: theme.roundness,
          marginTop: theme.spacing(1),
          elevation: 4,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          overflow: 'hidden',
        },
        listContainer: {
          maxHeight: 240,
        },
        buttonContainer: {
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: theme.spacing(2),
          marginTop: theme.spacing(2),
        },
        emptyText: {
          textAlign: 'center',
          color: theme.colors.onSurfaceVariant,
          marginTop: theme.spacing(2),
        },
        nodeItem: {
          paddingVertical: theme.spacing(1),
        },
      }),
    [theme]
  )

  const body = (
    <View>
      {loading ? (
        <Text style={styles.emptyText}>{tNodes('Loading')}</Text>
      ) : availableNodes.length === 0 ? (
        <Text style={styles.emptyText}>{t('no-other-nodes-available')}</Text>
      ) : (
        <View style={styles.selectorContainer}>
          <View style={styles.dropdownContainer}>
            <Chip
              icon={IconNode}
              mode="outlined"
              closeIcon={showDropdown ? 'menu-up' : 'menu-down'}
              onPress={() => setShowDropdown((prev) => !prev)}
              onClose={() => setShowDropdown((prev) => !prev)}
            >
              {selectedNode?.name || tNodes('no-node-selected')}
            </Chip>
            {showDropdown && (
              <View style={styles.dropdownList}>
                <Searchbar
                  placeholder={tNodes('search-nodes')}
                  onChangeText={setSearchQuery}
                  value={searchQuery}
                />
                <View style={styles.listContainer}>
                  <FlatList
                    data={filteredNodes}
                    renderItem={({ item }) => (
                      <List.Item
                        title={item.name}
                        description={
                          item.isPrivate ? t('private-node') : t('public-node')
                        }
                        left={(props) => (
                          <List.Icon {...props} icon={IconNode} />
                        )}
                        right={
                          selectedNode?.id === item.id
                            ? (props) => <List.Icon {...props} icon="check" />
                            : undefined
                        }
                        onPress={() => {
                          setSelectedNode(item)
                          setShowDropdown(false)
                          setSearchQuery('')
                        }}
                        style={styles.nodeItem}
                      />
                    )}
                    keyExtractor={(item) => item.id}
                    nestedScrollEnabled
                    scrollEnabled
                  />
                </View>
              </View>
            )}
          </View>
        </View>
      )}
    </View>
  )

  const actions = (
    <View style={styles.buttonContainer}>
      <Button mode="outlined" onPress={onDismiss} disabled={isTransferring}>
        {t('cancel')}
      </Button>
      <Button
        mode="contained"
        onPress={handleTransfer}
        loading={isTransferring}
        disabled={
          isTransferring || !selectedNode || availableNodes.length === 0
        }
      >
        {t('transfer')}
      </Button>
    </View>
  )

  return (
    <MelddModal
      visible={visible}
      onDismiss={onDismiss}
      title={t('transfer-cluster-to-node')}
      body={body}
      actions={actions}
    />
  )
}
